import type { Location } from "history";
import type React from "react";
import {
  Redirect,
  Route,
  type RouteComponentProps,
  type RouteProps,
  useLocation,
} from "react-router-dom";

import { useSchoolyearActive } from "hooks/useSchoolyearActive";

import { GENERIC_ROUTES } from "constants/routes";
import { schoolyearAllowedRoutes } from "constants/schoolyear-constants";

export interface AppRouteProps extends RouteProps {
  path: string;
  token?: string;
  publicRoute?: boolean;
  redirectPath?: string;
  component: React.FC<RouteComponentProps>;
}

interface Options {
  pathname: string;
  search?: string;
}

const redirect = (path: string, location: Location) => {
  let options: Options = { pathname: path };
  if (location) {
    options = {
      ...options,
      search: location.pathname !== "/" ? `?state=${location.pathname}` : location.search,
    };
  }
  return <Redirect to={options} />;
};

const AppRoute: React.FC<AppRouteProps> = ({
  component: Component,
  path = "",
  redirectPath = "",
  token,
  publicRoute,
  ...otherProps
}) => {
  const location = useLocation();
  const { isSchoolyearActive } = useSchoolyearActive();

  const isBlockedRoute = isSchoolyearActive && !schoolyearAllowedRoutes.includes(path);

  const renderRoute = (props: RouteComponentProps) => {
    // If Schoolyear is active and route path is not whitelisted, redirect to blocked path page
    if (isBlockedRoute) {
      return redirect(GENERIC_ROUTES.BLOCKED_PATH, location);
    }

    // If route is private and user hasn't logged in, take them to specified redirectPath
    if (!publicRoute && !token) {
      return redirect(redirectPath, location);
    }

    // In any other case, render actual content of the page
    return <Component {...props} />;
  };

  return <Route {...otherProps} path={path} render={renderRoute} />;
};

export default AppRoute;
